import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress, LinearProgress, Tooltip, Typography } from '@dls/react-core';
import {
  createGraphSettings,
  hasCategoricalData,
} from '../../../components/metrics/graph-settings';
import moment from 'moment';
import styled from 'styled-components';
import { MetricExporter } from '../../../components/subjects/metric-exporter';
import { Chip, IconButton } from '@material-ui/core';
import { Icon } from '@dls/react-icon';
import { MeasurementType, querySubjectData } from '../../../api/studykit';
import { Graph } from '../../../components/metrics/graph';
import { GraphSettings } from '../../../components/metrics/graph-single';

interface GraphContainerProps {
  measurementType: MeasurementType;
  zoomStart: Date;
  zoomEnd: Date;
  onResetZoom: () => void;
  onZoom: (range: [Date, Date]) => void;
  subjectId: string;
  studyId: string;
  showGaps: boolean;
}

const StyledGraphInfo = styled.div`
  display: flex;
  justify-content: end;
  width: 100%;
  visibility: hidden;
  align-items: center;
  column-gap: 16px;
  margin-bottom: 8px;
`;

const StyledGraphContainer = styled.div`
  position: relative;

  &:hover ${StyledGraphInfo} {
    visibility: visible;
  }
`;

const GraphHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const GraphContainer: React.FC<GraphContainerProps> = ({
  measurementType,
  zoomStart,
  zoomEnd,
  onZoom,
  onResetZoom,
  subjectId,
  studyId,
  showGaps,
}) => {
  const [fetchingData, setFetchingData] = useState(false);
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [lastFetchedRange, setLastFetchedRange] = useState<[Date, Date]>();

  const graphSettings = useMemo(() => createGraphSettings(measurementType), [measurementType]);
  const zoomAggregation = computeTimeBucketDuration(
    measurementType,
    zoomStart,
    zoomEnd,
    graphSettings,
  );

  useEffect(() => {
    setLastFetchedRange(undefined);
  }, [subjectId, measurementType, zoomAggregation]);

  useEffect(() => {
    // skip if new zoom range falls within the fetched data range AND zoom aggregation stayed the same
    if (lastFetchedRange && zoomStart >= lastFetchedRange[0] && zoomEnd <= lastFetchedRange[1]) {
      return;
    }

    setFetchingData(true);
    setLastFetchedRange([zoomStart, zoomEnd]);

    querySubjectData(studyId, subjectId, {
      measurementType: measurementType.name,
      bucketDuration: zoomAggregation,
      bucketDurationUnit: 'second',
      startAt: zoomStart,
      endAt: zoomEnd,
      aggregate: getAggregateFn(measurementType.name),
      fillGaps: true,
    })
      .then(setData)
      .finally(() => setFetchingData(false));
  }, [zoomAggregation, zoomStart, zoomEnd, measurementType, subjectId, lastFetchedRange, studyId]);

  return (
    <StyledGraphContainer>
      <GraphHeader>
        <Typography variant={'h4'} style={{ margin: 0, width: '100%' }}>
          {measurementType.label}
        </Typography>
        <StyledGraphInfo>
          {zoomAggregation > 0 && (
            <Tooltip title={<span>Aggregated every {zoomAggregation} seconds</span>}>
              <Chip
                label={`${zoomAggregation} seconds`}
                icon={<Icon name={'InformationCircle'} size={'m'} />}
              />
            </Tooltip>
          )}
          <div style={{ display: 'flex', columnGap: 4 }}>
            <IconButton
              title={'Zoom in'}
              size={'small'}
              onClick={() => {
                const diff = zoomEnd.getTime() - zoomStart.getTime();
                onZoom([
                  new Date(zoomStart.getTime() + diff / 4),
                  new Date(zoomEnd.getTime() - diff / 4),
                ]);
              }}
            >
              <Icon name={'ZoomIn'} />
            </IconButton>
            <IconButton
              title={'Zoom out'}
              size={'small'}
              onClick={() => {
                const diff = zoomEnd.getTime() - zoomStart.getTime();
                onZoom([
                  new Date(zoomStart.getTime() - diff / 2),
                  new Date(zoomEnd.getTime() + diff / 2),
                ]);
              }}
            >
              <Icon name={'ZoomOut'} />
            </IconButton>

            <IconButton title={'Reset zoom'} onClick={onResetZoom} size={'small'}>
              <Icon name={'Reset'} />
            </IconButton>
          </div>
          <MetricExporter>
            {({ isSubmitting, triggerExport }) => (
              <IconButton
                size={'medium'}
                title={'Export data'}
                onClick={() => triggerExport(zoomStart, zoomEnd, [measurementType.sourceFormatId])}
              >
                {isSubmitting ? (
                  <CircularProgress dls_size={'small'} variant={'indeterminate'} />
                ) : (
                  <Icon name={'DocumentDownload32'} size={'m'} />
                )}
              </IconButton>
            )}
          </MetricExporter>
        </StyledGraphInfo>
      </GraphHeader>

      <LinearProgress
        style={{ marginTop: 0, visibility: fetchingData && data?.length ? 'visible' : 'hidden' }}
        variant="indeterminate"
      />

      <Graph
        data={data}
        onZoom={onZoom}
        zoomRange={[zoomStart, zoomEnd]}
        onResetZoom={onResetZoom}
        loading={fetchingData}
        settings={graphSettings}
        connectNulls={!showGaps}
        maximumDataPoints={
          (zoomEnd.getTime() - zoomStart.getTime()) /
          1000 /
          (zoomAggregation || fixedIntervalInMillis(measurementType))
        }
      />
    </StyledGraphContainer>
  );
};

function fixedIntervalInMillis(measurementType: MeasurementType) {
  switch (measurementType.name) {
    case 'Acceleration':
    case 'Photoplethysmogram':
      return 1 / 32;
    case 'Electrocardiogram':
      return 1 / 256;
    default:
      return 1;
  }
}

function computeTimeBucketDuration(
  measurementType: MeasurementType,
  startAt: Date,
  endAt: Date,
  graphSettings: GraphSettings,
) {
  const duration = moment.duration(moment(endAt).diff(moment(startAt)));

  const buckets = [1, 5, 10, 30, 60, 120, 300, 600, 1800, 3600];
  // TODO move this to graph-settings?
  if (
    measurementType.name === 'Acceleration' ||
    measurementType.name === 'Photoplethysmogram' ||
    measurementType.name === 'Electrocardiogram'
  ) {
    if (duration.asSeconds() < 60) {
      return 0;
    }
  } else {
    buckets.unshift(0);
  }

  const max = graphSettings.yAxes.some((y) => y.graphType === 'bar') ? 200 : 1000;
  const computedDuration = Math.round(Math.max(duration.asSeconds() / max, 0));

  if (computedDuration > 3600) {
    return computedDuration;
  }

  // return closest bucket to the computed duration
  return buckets.reduce((prev, curr) => {
    return Math.abs(curr - computedDuration) < Math.abs(prev - computedDuration) ? curr : prev;
  });
}

function getAggregateFn(measurementType: string) {
  return hasCategoricalData(measurementType) ? 'mostFrequent' : 'avg';
}
